import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './Stats.css';

const Stats = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const statsConfig = useMemo(
    () => [
      {
        value: 44,
        format: (val) => `${Math.round(val)}M`,
        label: 'American households locked out of real estate wealth'
      },
      {
        value: 600,
        format: (val) => `$${Math.round(val)}B`,
        label: 'In annual rent payments generating zero equity'
      },
      {
        value: 90,
        format: (val) => `${Math.round(val)}%`,
        label: 'Of millionaires built wealth through real estate'
      },
      {
        value: 5,
        format: (val) => `${Math.round(val)}%`,
        label: 'Minimum investment to start building equity'
      }
    ],
    []
  );

  const [counters, setCounters] = useState(() => statsConfig.map(() => 0));

  useEffect(() => {
    setCounters(statsConfig.map(() => 0));
  }, [statsConfig]);

  useEffect(() => {
    if (!inView) return;

    const duration = 1600;
    const start = performance.now();

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

    let frameId;

    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = easeOutCubic(progress);

      setCounters(statsConfig.map((stat) => stat.value * eased));

      if (progress < 1) {
        frameId = requestAnimationFrame(animate);
      }
    };

    frameId = requestAnimationFrame(animate);

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [inView, statsConfig]);

  return (
    <section className="stats" ref={ref}>
      <div className="container">
        <motion.div 
          className="stats-grid"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {statsConfig.map((stat, index) => {
            const currentValue = counters[index] || 0;
            const formattedValue = stat.format(currentValue);

            return (
              <motion.div
                key={stat.label}
                className="stat"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <h3>{formattedValue}</h3>
                <p>{stat.label}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Stats;